import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, ALL_STATUSES, CATEGORY_COLORS, STATUS_LABEL, STATUS_COLORS, formatSeconds, competitionResultLabel, isHistoricalResult } from "@/lib/api";
import { CompetitionResultRow } from "@/components/CompetitionResultRow";
import { OrderSummary } from "@/components/OrderSummary";
import { ImportReport } from "@/components/ImportReport";
import { useCategories } from "@/context/CategoriesContext";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, MapPin, DollarSign, Users, Timer, Trophy,
  Upload, Download, Plus, Pencil, Trash2, X, CheckCircle2, Printer,
  QrCode, Copy, Mail, Send, UserX, Info, Phone, School, Shirt, Tag, Clock, FileSpreadsheet,
  RotateCcw, CreditCard,
} from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

const TABS = [
  { key: "competitors", label: "Competitors", icon: Users },
  { key: "notsignedin", label: "Not Signed In", icon: UserX },
  { key: "prelim", label: "Timing Round", icon: Timer },
  { key: "official", label: "Competition Round", icon: Trophy },
  { key: "checkin", label: "Check-In QR", icon: QrCode },
  { key: "invites", label: "Invites", icon: Mail, private: true },
  { key: "import", label: "Bulk Import", icon: Upload },
];

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [rankings, setRankings] = useState({});
  const [tab, setTab] = useState("competitors");
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});
  const latestEventId = useRef(id);
  latestEventId.current = id;

  const loadEvent = useCallback(() => api.get(`/events/${id}`).then((r) => {
    if (latestEventId.current !== id) return;
    setEvent(r.data); setLoadErrors((current) => ({ ...current, event: "" }));
  }).catch((error) => {
    if (latestEventId.current !== id) return;
    setEvent(null); setLoadErrors((current) => ({ ...current, event: error.response?.status === 404 ? "Event not found." : "Could not load the event. Please try again." }));
  }), [id]);
  const loadCompetitors = useCallback(() => api.get("/competitors", { params: { event_id: id } }).then((r) => {
    if (latestEventId.current !== id) return;
    setRows(r.data || []); setLoadErrors((current) => ({ ...current, roster: "" }));
  }).catch(() => {
    if (latestEventId.current !== id) return;
    setRows([]); setLoadErrors((current) => ({ ...current, roster: "Could not load the competitor roster." }));
  }), [id]);
  const loadRankings = useCallback(() => api.get("/rankings", { params: { event_id: id } }).then((r) => {
    if (latestEventId.current !== id) return;
    setRankings(r.data || {}); setLoadErrors((current) => ({ ...current, rankings: "" }));
  }).catch(() => {
    if (latestEventId.current !== id) return;
    setRankings({}); setLoadErrors((current) => ({ ...current, rankings: "Could not load standings." }));
  }), [id]);

  useEffect(() => {
    let active = true;
    setLoading(true); setEvent(null); setRows([]); setRankings({}); setLoadErrors({});
    Promise.all([loadEvent(), loadCompetitors(), loadRankings()]).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadEvent, loadCompetitors, loadRankings]);

  const refreshAll = () => { loadCompetitors(); loadRankings(); };

  if (loading) return <div className="text-[#7A7D8C]" data-testid="event-detail-loading">Loading…</div>;
  if (!event) return (
    <div>
      <Link to="/admin/events" className="text-sm text-[#FF9F1C] font-semibold inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to events</Link>
      <div role="alert" className="mt-6 bg-white rounded-2xl border border-slate-100 p-12 text-center text-[#7A7D8C]" data-testid="event-detail-error">{loadErrors.event || "Event not found."}<button type="button" onClick={loadEvent} className="ml-3 underline" data-testid="event-detail-retry">Try again</button></div>
    </div>
  );

  return (
    <div>
      {(loadErrors.roster || loadErrors.rankings) && <div role="alert" className="mb-5 rounded-xl border border-red-200 p-4 text-sm text-red-800" data-testid="event-data-error">
        {loadErrors.roster && <p data-testid="event-roster-error">{loadErrors.roster}</p>}{loadErrors.rankings && <p data-testid="event-rankings-error">{loadErrors.rankings}</p>}
        <button type="button" onClick={refreshAll} className="mt-2 font-semibold underline" data-testid="event-data-retry">Retry loading results</button>
      </div>}
      <Link to="/admin/events" data-testid="back-to-events" className="text-sm text-[#FF9F1C] font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"><ArrowLeft className="w-4 h-4" /> Back to events</Link>

      <div className="mt-4 bg-white rounded-3xl border border-slate-100 p-6 flex flex-wrap gap-5 items-start" data-testid="event-header">
        <DateBadge dateStr={event.start_date} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wider text-[#FF9F1C] font-black">Competition</span>
            {event.visibility === "invite_only" && <span className="text-[10px] uppercase tracking-wider font-bold text-[#B36906] px-2 py-0.5 border border-[#FDE74C] bg-[#FDE74C]/40 rounded-full" data-testid="ed-mock-badge">Mock · Invite only</span>}
          </div>
          <h1 className="mt-1 font-display font-black text-3xl tracking-tight" data-testid="event-title">{event.title}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#7A7D8C]">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDate(event.end_date)}` : ""}</span>
            {(event.start_time || event.end_time) && <span>{event.start_time}{event.end_time ? ` – ${event.end_time}` : ""}</span>}
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
            {event.registration_fee != null && <span className="inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {event.registration_fee}</span>}
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {rows.length} competitor{rows.length === 1 ? "" : "s"}</span>
            <span className="inline-flex items-center gap-1 text-[#0F7A72]"><CheckCircle2 className="w-3.5 h-3.5" /> {rows.filter((r) => r.status === "arrived").length} arrived</span>
          </div>
        </div>
        <Link to={`/admin/leaderboard?event=${id}`} data-testid="event-leaderboard-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF9F1C] text-sm font-bold text-[#B36906]"><Trophy className="w-4 h-4" /> Leaderboard</Link>
      </div>

      <div className="mt-6 flex gap-2 flex-wrap border-b border-slate-100" data-testid="event-tabs">
        {TABS.filter((t) => !t.private || event.visibility === "invite_only").map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`event-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-[#FF9F1C] text-[#FF9F1C]" : "border-transparent text-[#7A7D8C] hover:text-[#1E1E24]"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === "competitors" && <CompetitorsPanel event={event} rows={rows} onChanged={refreshAll} />}
        {tab === "notsignedin" && <NotSignedInPanel event={event} rows={rows} onChanged={refreshAll} />}
        {tab === "prelim" && <PrelimPanel rows={rows} onChanged={refreshAll} />}
        {tab === "official" && <OfficialPanel rankings={rankings} onChanged={refreshAll} />}
        {tab === "checkin" && <CheckinQRPanel event={event} rows={rows} />}
        {tab === "invites" && event.visibility === "invite_only" && <InvitesPanel event={event} />}
        {tab === "import" && <BulkImportPanel event={event} onDone={refreshAll} />}
      </div>
    </div>
  );
}

// ---- Date badge ----
function DateBadge({ dateStr }) {
  if (!dateStr) return null;
  let d;
  try { d = new Date(dateStr + "T00:00:00"); } catch { return null; }
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="shrink-0 w-20 rounded-2xl bg-white border-2 border-[#FF9F1C] shadow-[0_6px_16px_rgb(255,159,28,0.15)] overflow-hidden text-center" data-testid="event-date-badge">
      <div className="bg-[#FF9F1C] text-white text-[10px] font-black uppercase tracking-widest py-1">{month}</div>
      <div className="font-display font-black text-3xl leading-none pt-2 text-[#1E1E24]">{day}</div>
      <div className="text-[10px] text-[#7A7D8C] pb-2 pt-1 font-semibold">{year}</div>
    </div>
  );
}

// ---- Competitors tab ----
function CompetitorsPanel({ event, rows, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const removeOne = async (rid) => {
    if (!window.confirm("Delete this competitor?")) return;
    await api.delete(`/competitors/${rid}`);
    toast.success("Deleted");
    onChanged();
  };

  const exportCsv = async () => {
    try {
      const url = `${api.defaults.baseURL}/competitors/export.csv?event_id=${event.id}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Could not download CSV");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${event.title.replace(/[^a-z0-9]+/gi, "_")}_roster.csv`;
      a.click();
      toast.success("Roster CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="font-display font-bold text-lg">Roster ({rows.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportCsv} data-testid="ed-export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]"><FileSpreadsheet className="w-4 h-4" /> Export CSV</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="ed-add-competitor" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]">
            <Plus className="w-4 h-4" /> Add competitor
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ed-competitors-table">
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <Th>Name</Th><Th>Status</Th><Th>Parent</Th><Th>Parent-selected</Th><Th>Timing Solve</Th><Th>Recommended</Th><Th>Assigned</Th><Th>Competition result</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-[#7A7D8C]">No competitors yet. Add one above or use <b>Bulk Import</b>.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setViewing(r)} className="border-t border-slate-100 hover:bg-[#FFBF69]/10 cursor-pointer" data-testid={`ed-row-${r.id}`}>
                  <Td><div className="font-semibold">{r.full_name}</div><div className="text-xs text-[#7A7D8C]">{r.contact_email || r.contact_info}</div></Td>
                  <Td onClickStop><StatusControl row={r} onChanged={onChanged} /></Td>
                  <Td>{r.parent_name || "—"}<span className="block text-xs text-[#7A7D8C]" data-testid={`ed-experience-${r.id}`}>Previous competitions: {r.previous_competitions ?? "Not recorded"}</span></Td>
                  <Td><CatBadge value={r.parent_selected_category} /></Td>
                  <Td>{formatSeconds(r.prelim_average)}</Td>
                  <Td><CatBadge value={r.recommended_category} /></Td>
                  <Td><CatBadge value={r.assigned_category} /></Td>
                  <Td><span data-testid={`ed-roster-result-${r.id}`}>{r.official_dnf ? <span className="text-[#E71D36] font-semibold">DNF</span> : formatSeconds(r.official_average)}</span>{isHistoricalResult(r) && <span className="block text-[11px] text-[#7A7D8C]" data-testid={`ed-roster-format-${r.id}`}>Historical average</span>}</Td>
                  <Td onClickStop>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setViewing(r)} data-testid={`ed-view-${r.id}`} className="p-2 rounded-lg hover:bg-slate-100" title="View details"><Info className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`ed-edit-${r.id}`} className="p-2 rounded-lg hover:bg-slate-100" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeOne(r.id)} data-testid={`ed-delete-${r.id}`} className="p-2 rounded-lg hover:bg-red-50 text-[#E71D36]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <CompetitorForm
          initial={editing}
          eventId={event.id}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); onChanged(); }}
        />
      )}
      {viewing && <CompetitorDetailModal row={viewing} event={event} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); setShowForm(true); }} onChanged={onChanged} />}
    </div>
  );
}

// ---- Preliminary tab ----
function PrelimPanel({ rows, onChanged }) {
  const [saving, setSaving] = useState({});
  return (
    <div>
      <div className="mb-4">
        <h2 className="font-display font-bold text-lg">Timing Round</h2>
        <p className="text-[#7A7D8C] text-sm">Record each cuber&apos;s <b>one</b> Timing Solve. Leave blank for a DNF. This determines their category for the competition round.</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ed-prelim-table">
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <Th>Competitor</Th><Th>Parent-selected</Th><Th>Timing Solve</Th><Th>Recommended</Th><Th>Assigned</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[#7A7D8C]">Add competitors first.</td></tr>
              )}
              {rows.map((r) => <PrelimRow key={r.id} row={r} saving={saving[r.id]} setSaving={(v) => setSaving((s) => ({ ...s, [r.id]: v }))} onDone={onChanged} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PrelimRow({ row, saving, setSaving, onDone }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const existing = row.prelim_times && row.prelim_times.length > 0 && row.prelim_times[0] != null ? String(row.prelim_times[0]) : "";
  const [t, setT] = useState(existing);
  const [assigned, setAssigned] = useState(row.assigned_category || "");

  useEffect(() => { setAssigned(row.assigned_category || ""); }, [row.assigned_category]);
  const save = async () => {
    setSaving(true);
    try {
      const time = t.trim() === "" ? null : Number(t);
      if (time !== null && (!Number.isFinite(time) || time <= 0)) {
        toast.error("Enter a positive Timing Solve in seconds."); return;
      }
      const { data } = await api.put(`/competitors/${row.id}/prelim`, { times: [time] });
      toast.success(`${row.full_name}: ${data.prelim_average ?? "DNF"} → ${data.recommended_category ? CATEGORY_LABEL[data.recommended_category] : "—"}`);
      if (!data.recommended_category && assigned && assigned !== row.assigned_category && assigned !== data.assigned_category) {
        await api.patch(`/competitors/${row.id}`, { assigned_category: assigned });
      }
      onDone();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const acceptRecommended = async () => {
    if (!row.recommended_category) return;
    await api.patch(`/competitors/${row.id}`, { assigned_category: row.recommended_category });
    toast.success("Accepted recommendation");
    onDone();
  };

  const mismatched = row.parent_selected_category && row.recommended_category && row.parent_selected_category !== row.recommended_category;

  return (
    <tr className="border-t border-slate-100 hover:bg-[#FFBF69]/5" data-testid={`ed-prelim-row-${row.id}`}>
      <td className="px-3 py-3">
        <div className="font-semibold">{row.full_name}</div>
        {row.category_review_required && <div className="mt-1 text-xs font-semibold text-[#B36906]" data-testid={`ed-category-review-${row.id}`}>Timing Solve is 121s or above — manual placement needed</div>}
        {mismatched && <div className="text-[10px] uppercase font-bold text-[#E71D36] mt-0.5">Mismatch</div>}
      </td>
      <td className="px-3 py-3"><CatBadge value={row.parent_selected_category} /></td>
      <td className="px-2 py-3">
        <input data-testid={`ed-prelim-t1-${row.id}`} value={t} onChange={(e) => setT(e.target.value)} placeholder="s.ms" className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
      </td>
      <td className="px-3 py-3"><CatBadge value={row.recommended_category} /></td>
      <td className="px-3 py-3">
        <select value={assigned} disabled={Number(t) > 0 && Number(t) < 121} title={Number(t) > 0 && Number(t) < 121 ? "Assigned automatically when the Timing Solve is saved" : "Manual category placement"} onChange={(e) => setAssigned(e.target.value)} data-testid={`ed-prelim-assign-${row.id}`} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm bg-white">
          <option value="">—</option>
          {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-1 justify-end">
          {row.recommended_category && row.assigned_category !== row.recommended_category && (
            <button onClick={acceptRecommended} data-testid={`ed-prelim-accept-${row.id}`} className="p-2 rounded-lg text-[#2EC4B6] hover:bg-[#2EC4B6]/10" title="Accept recommendation"><CheckCircle2 className="w-4 h-4" /></button>
          )}
          <button onClick={save} disabled={saving} data-testid={`ed-prelim-save-${row.id}`} className="px-3 py-1.5 rounded-full bg-[#FF9F1C] text-white text-xs font-bold disabled:opacity-60">{saving ? "…" : "Save"}</button>
        </div>
      </td>
    </tr>
  );
}

// ---- Official tab ----
function OfficialPanel({ rankings, onChanged }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [openCat, setOpenCat] = useState("");
  useEffect(() => { if (!ALL_CATEGORIES.includes(openCat)) setOpenCat(ALL_CATEGORIES[0] || ""); }, [ALL_CATEGORIES, openCat]);
  const rows = rankings[openCat] || [];
  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-lg">Competition Round</h2>
          <p className="text-[#7A7D8C] text-sm" data-testid="ed-single-solve-instructions">Enter one competition solve in seconds. This time determines placement. Leave the time blank or select DNF / DNS if there is no valid solve. Historical results remain unchanged.</p>
        </div>
        <button onClick={() => window.print()} data-testid="ed-official-print" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]"><Printer className="w-4 h-4" /> Print rankings</button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {ALL_CATEGORIES.map((c) => (
          <button key={c} onClick={() => setOpenCat(c)} data-testid={`ed-official-tab-${c}`} className={`px-4 py-2 rounded-full text-sm font-semibold border ${openCat === c ? "bg-[#FF9F1C] text-white border-[#FF9F1C] shadow-[0_8px_20px_rgb(255,159,28,0.3)]" : "bg-white border-slate-200 hover:border-[#FF9F1C]"}`}>{CATEGORY_LABEL[c]} <span className="ml-2 text-xs opacity-70">{rankings[c]?.length ?? 0}</span></button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`ed-official-table-${openCat}`}>
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 w-14">Rank</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-2 py-3">Solve time (seconds)</th>
                <th className="text-left px-4 py-3">Result</th>
                <th className="text-left px-4 py-3">DNF / DNS</th>
                <th className="text-left px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-[#7A7D8C]">No competitors assigned to this category yet.</td></tr>
              ) : rows.map((r, i) => <CompetitionResultRow prefix="ed-official" key={r.id} row={r} rank={r.official_dnf || r.official_average == null ? "—" : i + 1} onDone={onChanged} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ---- Bulk Import tab ----
function BulkImportPanel({ event, onDone }) {
  const { ALL_CATEGORIES } = useCategories();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const downloadTemplate = async () => {
    try {
      const url = `${api.defaults.baseURL}/competitors/csv-template`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Could not download CSV");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "naptown_competitors_template.csv";
      a.click();
      toast.success("Template downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/competitors/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { event_id: event.id },
      });
      setImportReport(data);
      toast.success(`${data.created} created, ${data.updated || 0} updated, ${data.review?.length || 0} need review`);
      onDone();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Import failed. Check the CSV format and try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-8" data-testid="ed-import-panel">
      <div className="max-w-2xl">
        <h2 className="font-display font-bold text-xl">Bulk import competitors</h2>
        <p className="mt-2 text-[#7A7D8C] text-sm">Upload a CSV exported from Google Sheets. Rows are matched by record ID or unambiguous event/name/contact identity. Existing scores are never overwritten: conflicting or unclear rows are listed for review. Missing fields can be filled safely, and a backup is saved before any import writes.</p>

        <ol className="mt-6 space-y-4">
          <li className="flex gap-4">
            <span className="w-8 h-8 rounded-full bg-[#FF9F1C] text-white font-bold grid place-items-center shrink-0">1</span>
            <div className="flex-1">
              <div className="font-semibold">Download the template</div>
              <p className="text-xs text-[#7A7D8C]">Contains headers and one example row you can delete.</p>
              <button onClick={downloadTemplate} data-testid="ed-download-template" className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]">
                <Download className="w-4 h-4" /> Download roster template
              </button>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="w-8 h-8 rounded-full bg-[#FF9F1C] text-white font-bold grid place-items-center shrink-0">2</span>
            <div className="flex-1">
              <div className="font-semibold">Fill it out & upload</div>
              <p className="text-xs text-[#7A7D8C]" data-testid="ed-import-columns">Include <code>full_name</code>, school, grade, parent contact details, and category. Use <code>timing_solve_seconds</code> and <code>competition_solve_seconds</code> for durations; <code>event_date, event_start_time, event_end_time</code> are the event schedule, not solve times. Legacy <code>prelim_time</code>, <code>comp_time</code>, and five-solve columns remain supported. Keep record IDs and <code>result_format</code> when reimporting. Category IDs (kept for historical compatibility; use the ranges below for placement): <code>{ALL_CATEGORIES.join(", ")}</code>. Boundaries: less than 20; 20–under 41; 41–under 61; 61–under 91; 91–under 121 seconds. Decimal times stay in their labeled range without rounding. Values of 121 and above are kept for manual placement. Imported historical records may omit grade; new registrations require grades 3–12.</p>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={onFile} data-testid="ed-import-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ed-import-btn" className="mt-2 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload CSV"}
              </button>
            </div>
          </li>
        </ol>
        <ImportReport report={importReport} prefix="event-import" />
      </div>
    </div>
  );
}

// ---- Competitor form ----
function CompetitorForm({ initial, eventId, onClose, onSaved }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [form, setForm] = useState({
    full_name: initial?.full_name || "",
    parent_name: initial?.parent_name || "",
    contact_info: initial?.contact_info || "",
    parent_selected_category: initial?.parent_selected_category || "",
    assigned_category: initial?.assigned_category || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.parent_selected_category) delete payload.parent_selected_category;
      if (!payload.assigned_category) delete payload.assigned_category;
      if (initial) {
        await api.patch(`/competitors/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        delete payload.assigned_category;
        await api.post("/competitors", { ...payload, event_id: eventId });
        toast.success("Competitor added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
      <form onSubmit={save} className="bg-white rounded-3xl max-w-lg w-full p-8 relative my-8" data-testid="ed-competitor-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        <h2 className="font-display font-black text-2xl">{initial ? "Edit competitor" : "Add competitor"}</h2>
        <div className="mt-6 space-y-4">
          <Field label="Full name" value={form.full_name} required readOnly={initial?.name_source === "child_profile"} onChange={(v) => setForm({ ...form, full_name: v })} tid="ed-form-full-name" />
          {initial?.name_source === "child_profile" && <p className="text-xs text-[#7A7D8C]" data-testid="ed-linked-name-note">This name is managed by the linked child profile. Registration details below can still be edited.</p>}
          <Field label="Parent / guardian" value={form.parent_name} onChange={(v) => setForm({ ...form, parent_name: v })} tid="ed-form-parent" />
          <Field label="Contact info (email / phone)" value={form.contact_info} onChange={(v) => setForm({ ...form, contact_info: v })} tid="ed-form-contact" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Parent-selected category</label>
            <select data-testid="ed-form-parent-cat" value={form.parent_selected_category} onChange={(e) => setForm({ ...form, parent_selected_category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="">— none —</option>
              {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </select>
          </div>
          {initial && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Assigned category</label>
              <select data-testid="ed-form-assigned-cat" value={form.assigned_category} onChange={(e) => setForm({ ...form, assigned_category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                <option value="">— none —</option>
                {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Notes</label>
            <textarea data-testid="ed-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ed-form-save-btn" className="px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

// ---- Status control ----
function StatusControl({ row, onChanged }) {
  const current = row.status || "pending";
  const [saving, setSaving] = useState(false);
  const change = async (next) => {
    if (next === current) return;
    setSaving(true);
    try {
      await api.patch(`/competitors/${row.id}`, { status: next });
      toast.success(`${row.full_name}: ${STATUS_LABEL[next]}`);
      onChanged();
    } catch {
      toast.error("Status update failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_COLORS[current]}`} data-testid={`ed-status-badge-${row.id}`}>{STATUS_LABEL[current]}</span>
      <select
        value={current}
        onChange={(e) => change(e.target.value)}
        disabled={saving}
        data-testid={`ed-status-select-${row.id}`}
        className="rounded-lg border border-slate-200 px-2 py-1 text-xs bg-white disabled:opacity-60"
      >
        {ALL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
    </div>
  );
}

// ---- Check-in QR panel ----
function CheckinQRPanel({ event, rows }) {
  const { CATEGORY_LABEL } = useCategories();
  const url = `${window.location.origin}/checkin/${event.id}`;
  const arrived = rows.filter((r) => r.status === "arrived").length;
  const pending = rows.filter((r) => (r.status || "pending") === "pending").length;
  const noShow = rows.filter((r) => r.status === "no_show").length;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Check-in link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const printPoster = () => window.print();

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ed-checkin-panel">
      <div className="bg-white rounded-3xl border border-slate-100 p-8 print:shadow-none">
        <div className="text-[11px] uppercase tracking-widest font-black text-[#FF9F1C]">Event Check-In</div>
        <h2 className="mt-2 font-display font-black text-2xl">Scan to check in</h2>
        <p className="mt-1 text-sm text-[#7A7D8C]">Print this and post it at the door. Parents scan → sign in → pick which of their cubers arrived.</p>
        <div className="mt-6 grid place-items-center">
          <div className="rounded-3xl bg-white p-6 border-2 border-[#FF9F1C]/40 shadow-[0_10px_30px_rgb(255,159,28,0.15)]" data-testid="ed-checkin-qr">
            <QRCodeSVG value={url} size={220} level="M" bgColor="#FFFFFF" fgColor="#1E1E24" includeMargin={false} />
          </div>
        </div>
        <div className="mt-6 text-center font-display font-black text-lg">{event.title}</div>
        <div className="mt-1 text-center text-xs text-[#7A7D8C] break-all">{url}</div>
        <div className="mt-6 flex flex-wrap gap-2 justify-center print:hidden">
          <button onClick={copy} data-testid="ed-checkin-copy" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]"><Copy className="w-4 h-4" /> Copy link</button>
          <button onClick={printPoster} data-testid="ed-checkin-print" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Printer className="w-4 h-4" /> Print poster</button>
        </div>
      </div>

      <div className="print:hidden">
        <h3 className="font-display font-bold text-lg">Live status</h3>
        <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="ed-checkin-counters">
          <StatCard label="Arrived" value={arrived} accent="bg-[#2EC4B6]/15 text-[#0F7A72] border-[#2EC4B6]/30" tid="ed-count-arrived" />
          <StatCard label="Pending" value={pending} accent="bg-slate-100 text-slate-600 border-slate-200" tid="ed-count-pending" />
          <StatCard label="No-shows" value={noShow} accent="bg-[#E71D36]/10 text-[#E71D36] border-[#E71D36]/30" tid="ed-count-noshow" />
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Recent check-ins</div>
          <ul className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto" data-testid="ed-checkin-recent">
            {rows.filter((r) => r.status === "arrived" && r.checked_in_at)
              .sort((a, b) => (b.checked_in_at || "").localeCompare(a.checked_in_at || ""))
              .slice(0, 20)
              .map((r) => (
                <li key={r.id} className="px-5 py-3 flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#2EC4B6] shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm truncate">{r.full_name}</span>
                    <span className="block text-[11px] text-[#7A7D8C]">{new Date(r.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </span>
                  {r.assigned_category && <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${CATEGORY_COLORS[r.assigned_category]}`}>{CATEGORY_LABEL[r.assigned_category]}</span>}
                </li>
              ))}
            {rows.filter((r) => r.status === "arrived").length === 0 && (
              <li className="px-5 py-8 text-center text-sm text-[#7A7D8C]">No check-ins yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, tid }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent}`} data-testid={tid}>
      <div className="text-[11px] uppercase tracking-wider font-black">{label}</div>
      <div className="mt-1 font-display font-black text-3xl">{value}</div>
    </div>
  );
}

// ---- Invites panel ----
function InvitesPanel({ event }) {
  const [invites, setInvites] = useState([]);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/events/${event.id}/invites`).then((r) => setInvites(r.data || [])).catch(() => {});
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    const list = emails.split(/[\s,;\n]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/invites`, { emails: list });
      toast.success(`Sent ${data.invites?.length || 0} invite${(data.invites?.length || 0) === 1 ? "" : "s"}`);
      if (data.skipped?.length) toast.warning(`Skipped ${data.skipped.length} invalid`);
      setEmails("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invites");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inviteId) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await api.delete(`/events/invites/${inviteId}`);
      toast.success("Invite revoked");
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/events/invite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ed-invites-panel">
      <form onSubmit={send} className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-[#FF9F1C]" />
          <h2 className="font-display font-black text-xl">Send invitations</h2>
        </div>
        <p className="text-sm text-[#7A7D8C]">Add parent emails (comma, space, or newline separated). Each will receive a private link to access this mock competition.</p>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          rows={5}
          placeholder="alice@example.com, bob@example.com"
          data-testid="ed-invites-emails"
          className="mt-4 w-full rounded-2xl border border-slate-200 bg-[#FDFFFC] px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]"
        />
        <button type="submit" disabled={busy} data-testid="ed-invites-send" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send invites"}
        </button>
        <p className="mt-4 text-xs text-[#7A7D8C]">Parents will get an emailed link. If they don&apos;t have an account yet, they can create one when they click Accept — the invitation is auto-claimed on signup.</p>
      </form>

      <div>
        <h3 className="font-display font-bold text-lg">Sent ({invites.length})</h3>
        <div className="mt-4 bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {invites.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#7A7D8C]" data-testid="ed-invites-empty">No invitations yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto" data-testid="ed-invites-list">
              {invites.map((iv) => (
                <li key={iv.id} className="px-5 py-3 flex items-center gap-3" data-testid={`ed-invite-${iv.id}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${iv.accepted_at ? "bg-[#2EC4B6]" : "bg-[#FF9F1C]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{iv.email}</div>
                    <div className="text-[11px] text-[#7A7D8C]">
                      {iv.accepted_at ? `Accepted ${new Date(iv.accepted_at).toLocaleDateString()}` : `Sent ${new Date(iv.invited_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <button onClick={() => copyLink(iv.token)} data-testid={`ed-invite-copy-${iv.id}`} title="Copy link" className="p-2 rounded-lg hover:bg-slate-100"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => revoke(iv.id)} data-testid={`ed-invite-revoke-${iv.id}`} title="Revoke" className="p-2 rounded-lg hover:bg-red-50 text-[#E71D36]"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Not signed in tab ----
function NotSignedInPanel({ event, rows, onChanged }) {
  const [viewing, setViewing] = useState(null);
  const pending = rows.filter((r) => (r.status || "pending") === "pending");
  const total = rows.length;
  const arrived = rows.filter((r) => r.status === "arrived").length;
  const noShow = rows.filter((r) => r.status === "no_show").length;

  const markArrived = async (r, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/competitors/${r.id}`, { status: "arrived" });
      toast.success(`${r.full_name} → Arrived`);
      onChanged();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="ed-notsignedin-panel">
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Not signed in" value={pending.length} accent="bg-[#FDE74C]/40 text-[#7A5F00] border-[#FDE74C]" tid="ns-count-pending" />
        <StatCard label="Arrived" value={arrived} accent="bg-[#2EC4B6]/15 text-[#0F7A72] border-[#2EC4B6]/30" tid="ns-count-arrived" />
        <StatCard label="Total registered" value={total} accent="bg-slate-100 text-slate-700 border-slate-200" tid="ns-count-total" />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display font-bold text-lg">Awaiting check-in ({pending.length})</h2>
        <p className="text-xs text-[#7A7D8C]">Tap a competitor to view all details · use ✓ to check them in manually.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ed-notsignedin-table">
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <Th>Name</Th><Th>Parent</Th><Th>Parent email</Th><Th>Phone</Th><Th>Category</Th><Th>Registered</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-[#7A7D8C]">
                  {total === 0 ? "No competitors registered yet." : "🎉 Everyone is checked in."}
                </td></tr>
              ) : pending.map((r) => (
                <tr key={r.id} onClick={() => setViewing(r)} className="border-t border-slate-100 hover:bg-[#FFBF69]/10 cursor-pointer" data-testid={`ns-row-${r.id}`}>
                  <Td><div className="font-semibold">{r.full_name}</div>{noShow > 0 && r.status === "no_show" && <div className="text-[10px] uppercase font-bold text-[#E71D36]">No-show</div>}</Td>
                  <Td>{r.parent_name || "—"}<span className="block text-xs text-[#7A7D8C]" data-testid={`ed-experience-${r.id}`}>Previous competitions: {r.previous_competitions ?? "Not recorded"}</span></Td>
                  <Td className="max-w-[220px]">{r.contact_email || <span className="text-[#7A7D8C]">—</span>}</Td>
                  <Td>{r.contact_phone || <span className="text-[#7A7D8C]">—</span>}</Td>
                  <Td><CatBadge value={r.assigned_category || r.parent_selected_category} /></Td>
                  <Td className="text-xs text-[#7A7D8C]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</Td>
                  <Td onClickStop>
                    <button onClick={(e) => markArrived(r, e)} data-testid={`ns-arrive-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2EC4B6] text-white text-xs font-bold hover:brightness-110">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Check in
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && <CompetitorDetailModal row={viewing} event={event} onClose={() => setViewing(null)} onEdit={null} onChanged={onChanged} />}
    </div>
  );
}

// ---- Competitor detail modal ----
function CompetitorDetailModal({ row, event, onClose, onEdit, onChanged }) {
  const { CATEGORY_LABEL } = useCategories();
  const email = row.contact_email || (row.contact_info?.split(",").map((s) => s.trim()).find((s) => s.includes("@")) || "");
  const phone = row.contact_phone || (row.contact_info?.split(",").map((s) => s.trim()).find((s) => !s.includes("@") && s) || "");
  const officialSolves = isHistoricalResult(row) ? row.official_solves || [] : [row.official_solves?.[0] ?? null];
  const [registration, setRegistration] = useState(null);
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    api.get(`/competitors/${row.id}/registration`).then((r) => setRegistration(r.data || null)).catch(() => {});
  }, [row.id]);

  const refund = async () => {
    if (!registration?.id) return;
    if (!window.confirm(`Refund $${(registration.amount || 0).toFixed(2)} to ${row.full_name}'s parent? This cannot be undone.`)) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/registrations/${registration.id}/refund`);
      toast.success(`Refunded $${(data.amount || 0).toFixed(2)}`);
      setRegistration({ ...registration, payment_status: "refunded", refunded_at: new Date().toISOString(), refund_id: data.refund_id });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-[0_20px_60px_rgb(0,0,0,0.2)]" data-testid="competitor-detail-modal">
        <div className="p-8 border-b border-slate-100 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_COLORS[row.status || "pending"]}`}>{STATUS_LABEL[row.status || "pending"]}</span>
              {row.assigned_category && <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[row.assigned_category]}`}>{CATEGORY_LABEL[row.assigned_category]}</span>}
              {registration?.payment_status === "not_required" && <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#2EC4B6]/15 text-[#0F7A72]" data-testid="cd-free-chip">Free registration · Confirmed</span>}
              {registration?.payment_status === "paid" && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-[#2EC4B6]/15 text-[#0F7A72] border-[#2EC4B6]/30" data-testid="cd-paid-chip"><CreditCard className="w-3 h-3" /> Paid ${(registration.amount || 0).toFixed(2)}</span>}
              {registration?.payment_status === "refunded" && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-[#E71D36]/10 text-[#E71D36] border-[#E71D36]/30" data-testid="cd-refunded-chip"><RotateCcw className="w-3 h-3" /> Refunded</span>}
            </div>
            <h2 className="mt-2 font-display font-black text-3xl tracking-tight" data-testid="cd-name">{row.full_name}</h2>
            <p className="text-sm text-[#7A7D8C] mt-1">{event?.title}</p>
            {row.name_source === "child_profile" && row.original_full_name !== row.full_name && <p className="text-xs text-[#7A7D8C] mt-2" data-testid="cd-original-name">Originally registered as: {row.original_full_name}</p>}
          </div>
          <button onClick={onClose} data-testid="cd-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-8 space-y-6">
          <Section title="Contact">
            <DetailItem icon={<Users className="w-4 h-4" />} label="Parent / guardian" value={row.parent_name || "—"} tid="cd-parent" />
            <DetailItem icon={<Mail className="w-4 h-4" />} label="Parent email" value={email || "—"} tid="cd-email" copy={email} />
            <DetailItem icon={<Phone className="w-4 h-4" />} label="Phone" value={phone || "—"} tid="cd-phone" copy={phone} />
          </Section>

          <Section title="Cuber profile">
            <DetailItem label="Previous Naptown competitions" value={row.previous_competitions ?? "Not recorded"} tid="cd-experience" />
            <DetailItem label="Grade at registration" value={row.grade ?? "Not recorded"} tid="cd-grade" />
            <DetailItem icon={<School className="w-4 h-4" />} label="School" value={row.current_school || "—"} tid="cd-school" />
            <DetailItem icon={<Shirt className="w-4 h-4" />} label="Shirt size" value={row.shirt_size || "—"} tid="cd-shirt" />
            <DetailItem icon={<Clock className="w-4 h-4" />} label="Cuber time estimate" value={row.parent_selected_category ? CATEGORY_LABEL[row.parent_selected_category] : row.estimated_time_average || "—"} tid="cd-est" />
            <DetailItem icon={<Tag className="w-4 h-4" />} label="Parent-selected category" value={row.parent_selected_category ? CATEGORY_LABEL[row.parent_selected_category] : "—"} tid="cd-parent-cat" />
          </Section>

          <Section title="Rounds">
            <DetailItem label="Timing Solve" value={formatSeconds(row.prelim_average) || "—"} tid="cd-prelim" />
            <DetailItem label="Recommended" value={row.recommended_category ? CATEGORY_LABEL[row.recommended_category] : "—"} tid="cd-recommended" />
            <DetailItem label="Assigned category" value={row.assigned_category ? CATEGORY_LABEL[row.assigned_category] : "—"} tid="cd-assigned" />
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{isHistoricalResult(row) ? "Historical solves" : "Competition solve"}</div>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="cd-official-solves">
                {officialSolves.map((s, i) => (
                  <span key={`${row.id}-attempt-${i + 1}`} className={`inline-block px-2.5 py-1 rounded-lg text-xs font-mono border ${s == null ? "bg-slate-50 text-[#7A7D8C] border-slate-200" : "bg-white border-[#FFBF69]"}`}>S{i + 1}: {s == null ? "—" : formatSeconds(s)}</span>
                ))}
              </div>
              <div className="mt-2 text-sm"><b>{competitionResultLabel(row)}:</b> {row.official_dnf ? <span className="text-[#E71D36] font-bold">DNF</span> : (formatSeconds(row.official_average) || "—")}</div>
            </div>
          </Section>

          {registration?.order_summary && <OrderSummary summary={registration.order_summary} prefix="cd-order" paid={registration.payment_status === "paid" || registration.payment_status === "refunded"} />}
          {registration && (
            <Section title="Payment">
              <DetailItem icon={<CreditCard className="w-4 h-4" />} label="Amount" value={`$${(registration.amount || 0).toFixed(2)}`} tid="cd-amount" />
              <DetailItem label="Status" value={registration.payment_status === "not_required" ? "Confirmed — no payment required" : registration.payment_status || "—"} tid="cd-pay-status" />
              {registration.paid_at && <DetailItem label="Paid at" value={new Date(registration.paid_at).toLocaleString()} tid="cd-paid-at" />}
              {registration.refunded_at && <DetailItem label="Refunded at" value={new Date(registration.refunded_at).toLocaleString()} tid="cd-refunded-at" />}
            </Section>
          )}

          {row.checked_in_at && (
            <div className="rounded-2xl bg-[#2EC4B6]/10 border border-[#2EC4B6]/30 px-5 py-4 text-sm inline-flex items-center gap-2" data-testid="cd-checked-in">
              <CheckCircle2 className="w-4 h-4 text-[#2EC4B6]" />
              Checked in {new Date(row.checked_in_at).toLocaleString()}
            </div>
          )}

          {row.notes && (
            <Section title="Notes">
              <p className="text-sm text-[#1E1E24] whitespace-pre-wrap" data-testid="cd-notes">{row.notes}</p>
            </Section>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Close</button>
          {registration?.payment_status === "paid" && (
            <button onClick={refund} disabled={refunding} data-testid="cd-refund-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#E71D36] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(231,29,54,0.25)] disabled:opacity-60">
              <RotateCcw className="w-4 h-4" /> {refunding ? "Refunding…" : `Refund $${(registration.amount || 0).toFixed(2)}`}
            </button>
          )}
          {onEdit && <button onClick={onEdit} data-testid="cd-edit-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Pencil className="w-4 h-4" /> Edit</button>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-widest font-black text-[#7A7D8C] mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
}

function DetailItem({ icon, label, value, tid, copy }) {
  const onCopy = async () => {
    if (!copy) return;
    try { await navigator.clipboard.writeText(copy); toast.success("Copied"); }
    catch { toast.error("Could not copy. Please select and copy the text manually."); }
  };
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C] flex items-center gap-1.5">{icon}{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <div className="text-sm font-semibold break-all" data-testid={tid}>{value}</div>
        {copy && value && value !== "—" && <button onClick={onCopy} data-testid={`${tid || label.toLowerCase().replace(/\s+/g, "-")}-copy`} aria-label={`Copy ${label}`} className="p-1 rounded hover:bg-slate-100" title="Copy"><Copy className="w-3 h-3 text-[#7A7D8C]" /></button>}
      </div>
    </div>
  );
}

// ---- Small primitives ----
const Th = ({ children }) => <th className="text-left px-4 py-3 font-semibold">{children}</th>;
const Td = ({ children, onClickStop, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`} onClick={onClickStop ? (e) => e.stopPropagation() : undefined}>
    {children}
  </td>
);

function CatBadge({ value }) {
  const { CATEGORY_LABEL } = useCategories();
  if (!value) return <span className="text-xs text-[#7A7D8C]">—</span>;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[value] || ""}`}>{CATEGORY_LABEL[value]}</span>;
}

function Field({ label, value, onChange, required, readOnly = false, type = "text", tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} type={type} value={value} required={required} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
    </div>
  );
}
